import { Paper, Group, Text, ThemeIcon, Badge } from '@mantine/core';
import { IconTrendingUp, IconTrendingDown } from '@tabler/icons-react';

interface StatCardProps {
  icon: React.ReactNode | React.ComponentType<any>;
  label: string;
  value: string | number;
  trend?: number;
  trendLabel?: string;
  color?: string;
  variant?: 'default' | 'gradient' | 'outlined';
  onClick?: () => void;
}

export default function StatCard({
  icon,
  label,
  value,
  trend,
  trendLabel,
  color = 'brand',
  variant = 'default',
  onClick,
}: StatCardProps) {
  const isPositive = trend !== undefined && trend >= 0;

  return (
    <Paper
      p="lg"
      shadow="sm"
      style={{
        borderLeft: variant === 'default' ? `3px solid var(--mantine-color-${color}-5)` : undefined,
        background:
          variant === 'gradient'
            ? `linear-gradient(135deg, var(--mantine-color-${color}-0) 0%, var(--mantine-color-white) 100%)`
            : undefined,
        border: variant === 'outlined' ? '1px solid var(--mantine-color-gray-3)' : undefined,
        transition: 'transform 150ms ease, box-shadow 150ms ease',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
      }}
      onClick={onClick}
      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--mantine-shadow-sm)';
      }}
    >
      <Group justify="space-between" mb="xs">
        <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.04em' }}>
          {label}
        </Text>
        <ThemeIcon color={color} variant="light" size={52} radius="xl">
          {typeof icon === 'function' ? (() => { const Icon = icon; return <Icon size={24} />; })() : icon}
        </ThemeIcon>
      </Group>

      <Text fw={800} size="1.75rem" mb={4} lh={1.2}>
        {value}
      </Text>

      {trend != null && (
        <Group gap={8} mt={4}>
          <Badge
            variant="light"
            color={isPositive ? 'brand' : 'red'}
            size="sm"
            leftSection={
              isPositive ? (
                <IconTrendingUp size={12} />
              ) : (
                <IconTrendingDown size={12} />
              )
            }
          >
            {isPositive ? '+' : ''}
            {trend.toFixed(1)}%
          </Badge>
          {trendLabel && (
            <Text size="xs" c="dimmed">
              {trendLabel}
            </Text>
          )}
        </Group>
      )}
    </Paper>
  );
}
